import { useQuery, useQueryClient, type QueryObserverResult } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { completeOauth } from '../../api/integrations-client/integrations-client';
import { useIntegrationUrl, useStartIntegrationProcess } from '../../api/integrations-client/integrations-client.hooks';
import { IntegrationType } from '../../api/integrations-client/integrations-client.type';
import { UserContext } from '../../contexts';
import { isAllowedOAuthRedirectUrl } from '../../helpers/general-helpers';
import { useRunOnce } from '../../hooks/useRunOnce';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useOrgId } from '../../store/global-store/global-store.hooks';
import { useIntegrationsStore } from '../../store/integrations-store/integrations-store';
import {
  clearIsReauth,
  setIsReauth,
  updateIntegration,
} from '../../store/integrations-store/integrations-store.actions';
import { completeReauth, getIntegrationForReauth, getServiceForIntegrationType } from './integrations.helpers';

/**
 * Custom hook to handle re-authentication for a particular integration.
 *
 * @param {IntegrationType} integration - The type of integration to authenticate.
 * @returns {{ authenticate: () => void }} An object with an authenticate function.
 */
const useIntegrationAuth = (integration: IntegrationType): { authenticate: () => void } => {
  const { user } = useContext(UserContext);
  const organizationId = useGlobalStore((state) => state.organization?.id);

  const { url, query } = useIntegrationUrl({
    queryKey: ['integrationUrl', getServiceForIntegrationType(integration)] as const,
    enabled: false,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies(user): ignore
  useEffect(() => {
    if (url && organizationId && isAllowedOAuthRedirectUrl(url)) {
      const receivedUrl = new URL(url);
      const params = new URLSearchParams(receivedUrl.search);

      params.set('state', `${url},${organizationId}`);
      const updatedQueryString = params.toString();

      // Set re-authentication flag for OAuth return processing
      setIsReauth(true);

      window.location.href = `${receivedUrl.origin}${receivedUrl.pathname}?${updatedQueryString}`;
    }
  }, [url, user, organizationId]);

  return { authenticate: query.refetch };
};

/**
 * Custom hook to handle basic/token authentication for integrations.
 *
 * Modeled on `useCompleteOauth` for symmetry: fires the start-integration
 * mutation once, then reacts to settle state via a `useEffect`. Handling
 * navigation in an effect (instead of inline after `await`) ensures nothing
 * fires after the component has unmounted — otherwise sidebar nav clicks
 * during the in-flight `/integrations/processes` call get clobbered when the
 * closure resumes and pushes the user back to `/application/integrations/success`.
 *
 * IMPORTANT: do NOT add `navigate('/application/integrations/success')` to the
 * happy path. The user is already on that route (the authorize page navigates
 * there before this hook fires). A redundant navigate inside an awaited closure
 * is exactly what caused the original bug.
 *
 * @template T - The type of data returned on successful authentication
 * @param {Object} params - Hook parameters
 * @param {IntegrationType} params.integrationType - The integration type to authenticate with
 * @param {(data: T) => void} [params.onSuccess] - Optional callback invoked when the mutation succeeds
 * @param {boolean} [params.enabled] - Gate that prevents firing until the parent decides this hook should run
 * @returns {{ isLoading: boolean }} Loading state of the start-integration mutation
 */
const useBasicAuth = <T>({
  integrationType,
  onSuccess,
  enabled,
}: {
  integrationType: IntegrationType;
  onSuccess?: (data: T) => void;
  enabled?: boolean;
}): { isLoading: boolean } => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const integration = useIntegrationsStore((state) => state.integration);
  const orgId = useOrgId();

  const service = getServiceForIntegrationType(integrationType);
  const isValidData = Boolean(
    service && orgId && integration?.baseUrl && integration?.system_access_id && integration?.id && enabled,
  );

  const { mutate: startIntegrationProcess, isPending, isSuccess, isError, data } = useStartIntegrationProcess<T>();

  // Latches that make settle/notify idempotent for the lifetime of this mount.
  // Required because `useNavigate` is not referentially stable in React Router 7
  // when used under `BrowserRouter`, so `handleSettled`'s identity churns each
  // render and the settle effect would otherwise fire repeatedly. Same shape
  // also protects the caller's `onSuccess` from re-firing on identity churn.
  const hasSettled = useRef(false);
  const hasNotifiedOnSuccess = useRef(false);

  /**
   * Post-completion handler. Mirrors `useCompleteOauth`'s `handleSettled` and is
   * driven from a mount-guarded `useEffect` below — a no-op if the user has
   * already navigated away.
   *
   * Reads `isReauth` via `useIntegrationsStore.getState()` (rather than
   * subscribing) so the callback's branch is decided at invocation time, not
   * at memoization time — avoids a stale-closure path where mid-flight flag
   * flips would route to the wrong destination.
   *
   * @param {boolean} succeeded - Whether the mutation resolved successfully.
   * @returns {void}
   */
  const handleSettled = useCallback(
    (succeeded: boolean): void => {
      const isReauth = useIntegrationsStore.getState().isReauth;

      if (isReauth) {
        // Mirrors `useCompleteOauth`'s ordering exactly:
        // completeReauth → navigate → invalidate → clearIsReauth.
        completeReauth();
        navigate('/application/data-management');
        queryClient.invalidateQueries({ queryKey: ['integration-entries'] });
        clearIsReauth();
        return;
      }

      // Initial-setup flow — user is already on /application/integrations/success
      // (the authorize page routes there before this hook fires). Only redirect
      // on error.
      if (!succeeded) {
        navigate('/application/integrations');
      }
    },
    [navigate, queryClient],
  );

  // Fire the mutation once when valid data is available. `useRunOnce` guards
  // against duplicate runs (StrictMode, dep churn). We deliberately call
  // `mutate` (not `mutateAsync`) so navigation never lives inside an awaited
  // closure that would survive component unmount.
  useRunOnce(
    () => {
      // Runtime narrowing for TypeScript — `isValidData` (passed as `shouldRun`)
      // implies these are all truthy at runtime, but TS can't see through the
      // boolean coercion to narrow the optional/nullable fields.
      if (!service || !orgId || !integration?.baseUrl || !integration?.system_access_id || !integration?.id) {
        return;
      }

      const payload = {
        service,
        state: integration.baseUrl,
        organization_id: orgId,
        system_access_id: integration.system_access_id,
        integration_id: integration.id,
      };

      // Clear `system_access_id` synchronously *before* firing the mutation so
      // that any remount during the in-flight POST (sidebar nav click, browser
      // back/forward, reload) sees `isValidData === false` and `useRunOnce`
      // bails. The integration store is persisted to localStorage, so without
      // this latch a remount would observe the same valid data and fire a
      // duplicate `/processes` call — the original bug, narrower trigger.
      // The store change does not invalidate `payload`; it's already a snapshot.
      updateIntegration({ system_access_id: null });

      startIntegrationProcess(payload);
    },
    [service, orgId, integration?.baseUrl, integration?.system_access_id, integration?.id, startIntegrationProcess],
    isValidData,
  );

  // Invoke caller's `onSuccess` from a mount-guarded effect, mirroring
  // `useCompleteOauth`. Passing `onSuccess` directly to `useStartIntegrationProcess`
  // would fire it even after the component had unmounted — the same trap that
  // caused the original navigate-after-unmount bug. The `hasNotifiedOnSuccess`
  // latch ensures inline-callback identity churn from the caller doesn't
  // re-invoke the callback after it has already fired once. Latch is set
  // *after* the call so a thrown callback doesn't poison subsequent retries
  // (we still get a re-attempt on the next identity change).
  useEffect(() => {
    if (hasNotifiedOnSuccess.current) {
      return;
    }
    if (onSuccess && isSuccess && data !== undefined) {
      onSuccess(data);
      hasNotifiedOnSuccess.current = true;
    }
  }, [isSuccess, data, onSuccess]);

  // Settle once per mount. Latched against `useNavigate`-driven identity churn
  // of `handleSettled` (see `hasSettled` declaration above). Latch flips after
  // `handleSettled` returns so a throw doesn't strand the user on `/success`.
  useEffect(() => {
    if (hasSettled.current) {
      return;
    }
    if (isSuccess) {
      handleSettled(true);
      hasSettled.current = true;
    } else if (isError) {
      handleSettled(false);
      hasSettled.current = true;
    }
  }, [isSuccess, isError, handleSettled]);

  return {
    isLoading: isPending,
  };
};

/**
 * Universal OAuth hook that handles OAuth completion for all integration types.
 * Replaces the need for individual integration-specific OAuth hooks.
 *
 * @param params Hook parameters
 * @param params.integrationType The type of integration (jira, github, etc.)
 * @param [params.onSuccess] Callback called when OAuth completes successfully
 * @param [params.enabled] Whether to enable the OAuth query
 * @returns Object containing the OAuth query result and processing state
 */
const useCompleteOauth = ({
  integrationType,
  onSuccess,
  enabled,
}: {
  integrationType: IntegrationType;
  onSuccess?: (data: Record<string, unknown>) => void;
  enabled?: boolean;
}): {
  completeOauthQuery: QueryObserverResult<Record<string, unknown>>;
  isProcessing: boolean;
} => {
  const { user } = useContext(UserContext);
  const organization = useGlobalStore((state) => state.organization);
  const integration = useIntegrationsStore((state) => state.integration);
  const isReauth = useIntegrationsStore((state) => state.isReauth);

  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const service = getServiceForIntegrationType(integrationType);

  if (!service) {
    console.warn(`No service mapping found for integration type: ${integrationType}`);
  }

  const completeOauthQuery = useQuery({
    queryKey: ['oauth', integrationType, user?.id] as const,
    queryFn: async () => {
      const code = searchParams.get('code');
      const tempStates = searchParams.get('state')?.split(',');
      const state = tempStates ? tempStates[0] : '';
      const orgId = tempStates ? tempStates[1] : organization?.id;

      if (!user?.id || !orgId || !code || !state) {
        throw new Error('Invalid OAuth parameters for OAuth completion');
      }

      return await completeOauth(
        service!,
        user.id.toString(),
        orgId,
        code,
        state,
        integration?.id || getIntegrationForReauth() || null,
        {},
      );
    },
    enabled: !!searchParams.get('code') && !!user && !!service && enabled,
    retry: false,
  });

  // Handle cleanup and redirect logic after OAuth completion
  const handleSettled = useCallback(
    (isSuccess: boolean) => {
      // Clean up URL parameters
      const paramsToDelete = ['code', 'state', 'RESPONSE_TYPE', 'STATE', 'SCOPE', 'REDIRECT_URI'];
      paramsToDelete.forEach((param) => {
        searchParams.delete(param);
      });
      setSearchParams(searchParams);

      // Handle redirect logic based on flow type
      if (isReauth) {
        // Re-authentication flow - redirect to data management regardless of success/failure
        // The user needs to return to data management to see the status
        completeReauth(); // Clean up re-auth tracking
        navigate('/application/data-management'); // Always go back to data management for re-auth

        // Refetch integrations to update statuses
        queryClient.invalidateQueries({ queryKey: ['integration-entries'] });

        clearIsReauth(); // Clear flag for next use
      } else {
        // Initial setup flow - only go to success page on successful completion
        if (isSuccess) {
          navigate('/application/integrations/success');
        } else {
          // On error, redirect back to integrations page
          navigate('/application/integrations');
        }
      }
    },
    [isReauth, navigate, searchParams, setSearchParams, queryClient],
  );

  // Handle successful OAuth completion
  useEffect(() => {
    if (onSuccess && completeOauthQuery.isSuccess && completeOauthQuery.data) {
      onSuccess(completeOauthQuery.data);
    }
  }, [completeOauthQuery.isSuccess, completeOauthQuery.data, onSuccess]);

  // Handle OAuth completion (success or error) and trigger redirect logic
  useEffect(() => {
    if (completeOauthQuery.isSuccess) {
      handleSettled(true);
    } else if (completeOauthQuery.isError) {
      handleSettled(false);
    }
  }, [completeOauthQuery.isSuccess, completeOauthQuery.isError, handleSettled]);

  return {
    completeOauthQuery,
    isProcessing: completeOauthQuery.isPending,
  };
};

export { useBasicAuth, useCompleteOauth, useIntegrationAuth };
